import { Component, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MAT_DATE_FORMATS } from "@angular/material/core";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSort, Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { AuthService } from "app/auth/auth.service";
import { IClientData } from "app/models/303/clientData";
import { ISB303 } from "app/models/303/sb303";
import { IServiceCenterData } from "app/models/303/serviceCenterData";
import { SB303Service } from "app/services/303/303.service";
import { SharedService } from "app/services/core/shared.service";
import { ToastrService } from "ngx-toastr";
import { DialogViewClientDataComponent } from "./dialogs/dialog-view-client-data/dialog-view-client-data.component";
import { DialogViewServiceCenterDataComponent } from "./dialogs/dialog-view-service-center-data/dialog-view-service-center-data.component";
import { DialogUploadDeIdentifiedDataComponent } from "./dialogs/dialog-upload-deidentified-data/dialog-upload-deidentified-data.component";
import { ISB303DeIdentifiedData } from "app/models/303/sb303DeIdentifiedData";
import { DialogCreateNewReportComponent } from "./dialogs/dialog-create-new-report/dialog-create-new-report.component";
import { INonReportingLicense } from "app/models/303/non-reporting-license";
import { IApplicantData } from "app/models/303/applicantData/applicantData";
import { ApplicantDataService } from "app/services/applicant-data/applicant-data.service";
import { IApplicantDataSearch } from "app/models/303/applicantData/applicantDataSearch";
import { DialogDownloadReportingWindowDataComponent } from "./dialogs/dialog-download-reporting-window-data/dialog-download-reporting-window-data.component";
import { DialogDownloadIndividualServiceCenterDataComponent } from "./dialogs/dialog-download-individual-service-center-data/dialog-download-individual-service-center-data.component";
import { ClientDataAuditLogDialogComponent } from "../audit-log/client-data-audit-log/client-data-audit-log.component";
import { ServiceCenterDataAuditLogDialogComponent } from "../audit-log/service-center-data-audit-log/service-center-data-audit-log.component";
import { DialogSB303NotesComponent } from "./dialogs/dialog-sb303-notes/dialog-sb303-notes.component";
import { DialogPrintCompliancePDFComponent } from "./dialogs/print-compliance-pdf/print-compliance-pdf.component";

export const DateFormats = {
  parse: {
    dateInput: ['MM/DD/YYYY']
  },
  display: {
    dateInput: 'MM/DD/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
    selector: 'app-303-portal',
    templateUrl: './303.component.html',
    styleUrls: ['./303.component.scss'],
    providers: [{ provide: MAT_DATE_FORMATS, useValue: DateFormats }],
    standalone: false
})

export class SB303Component implements OnInit {

  public sb303Records: ISB303[] = [];
  public clientData: IClientData[] = [];
  public serviceCenterData: IServiceCenterData[] = [];
  public sb303DeIdentifiedData: ISB303DeIdentifiedData[] = [];
  public nonReportingLicenses: INonReportingLicense[] = [];
  public totalApplicantRecords = 0;
  public currentSort: Sort = { active: 'name', direction: 'asc' };
  public showNewFields: boolean = false;
  public showAveragesFields: boolean = false;

  public clientDataColumns: string[] = ['licenseId', 'entityName', 'submitted', 'submittedDate', 'open', 'reportingStartDate', 'reportingEndDate', 'partialQuarter', 'auditLog', 'actions'];
  public clientDataSource = new MatTableDataSource<IClientData>(this.clientData);
  @ViewChild('clientDataPaginator', { static: false }) clientDataPaginator: MatPaginator;
  @ViewChild('clientDataSort', { static: false }) clientDataSort: MatSort;

  public serviceCenterDataColumns: string[] = ['licenseId', 'entityName', 'submitted', 'submittedDate', 'open', 'reportingStartDate', 'reportingEndDate', 'partialQuarter', 'auditLog', 'actions'];
  public serviceCenterDataSource = new MatTableDataSource<IServiceCenterData>(this.serviceCenterData);
  @ViewChild('serviceCenterDataPaginator', { static: false }) serviceCenterDataPaginator: MatPaginator;
  @ViewChild('serviceCenterDataSort', { static: false }) serviceCenterDataSort: MatSort;

  public deIdentifiedDataColumns: string[] = ['startDate', 'endDate', 'actions'];
  public deIdentifiedDataSource = new MatTableDataSource<ISB303DeIdentifiedData>(this.sb303DeIdentifiedData);
  @ViewChild('deIdentifiedDataPaginator', { static: false }) deIdentifiedDataPaginator: MatPaginator;
  @ViewChild('deIdentifiedDataSort', { static: false }) deIdentifiedDataSort: MatSort;

  public licenseColumns: string[] = ['licenseId', 'entityName', 'primaryContactName', 'reportingStartDate', 'reportingEndDate'];
  public licenseSource = new MatTableDataSource<INonReportingLicense>(this.nonReportingLicenses);
  @ViewChild('licensePaginator', { static: false }) licensePaginator: MatPaginator;
  @ViewChild('licenseSort', { static: false }) licenseSort: MatSort;

  public applicantDataColumns: string[] = ['name', 'licenseId', 'applicantStatus', 'submittedDate'];
  public applicantDataSource = new MatTableDataSource<IApplicantData>([]);
  @ViewChild('applicantDataPaginator', { static: false }) applicantDataPaginator: MatPaginator;
  @ViewChild('applicantDataSort', { static: false }) applicantDataSort: MatSort;

  public dateForm = new FormGroup({
    from: new FormControl('', Validators.required),
    to: new FormControl('', Validators.required)
  });

  public serviceCenterForm = new FormGroup({
    id: new FormControl('', Validators.required)
  });

  public applicantDataSearchForm = new FormGroup({
    name: new FormControl(''),
    licenseId: new FormControl(''),
    submittedDate: new FormControl('')
  });

  constructor(public sharedService: SharedService,
    public sb303Service: SB303Service,
    public applicantDataService: ApplicantDataService,
    public authService: AuthService,
    public toastr: ToastrService,
    public dialog: MatDialog,) { }

  ngOnInit(): void {
    this.sb303Service.getDeIdentifiedData().subscribe({
      next: response => this.sb303DeIdentifiedData = response,
      error: e => console.log('error', e),
      complete: () => {
        this.updateDeIdentifiedTable();
      }
    });

    this.loadApplicantData();
  }

  submittedOnTime(submittedDate: string, endDate: string): boolean {
    const submitted = new Date(submittedDate);
    const end = new Date(endDate);
    return submitted < end;
  }

  getRecordsByDate(): void {
    let form = this.dateForm.value;
    let from = new Date(form.from).toDateString();
    let to = new Date(form.to).toDateString();

    this.sb303Service.getSB303Records(from, to).subscribe({
      next: response => this.sb303Records = response,
      error: e => console.log('error', e),
      complete: () => {
        this.serviceCenterData = this.sb303Records.map(record => ({
          ...record.serviceCenterData,
          reportingStartDate: record.reportingStartDate,
          reportingEndDate: record.reportingEndDate
        }));

        this.clientData = this.sb303Records.map(record => ({
          ...record.clientData,
          reportingStartDate: record.reportingStartDate,
          reportingEndDate: record.reportingEndDate
        }));
        this.updateTables();
      }
    });

    this.sb303Service.getNonReportingLicense(from, to).subscribe({
      next: response => this.nonReportingLicenses = response,
      error: e => console.log('error', e),
      complete: () => {
        this.updateNonReportingLicensesTable();
      }
    });
  }

  getRecordsById(): void {
    let form = this.serviceCenterForm.value;
    let id = form.id;

    this.sb303Service.getSB303RecordsById(id).subscribe({
      next: response => this.sb303Records = response,
      error: e => console.log('error', e),
      complete: () => {
        this.serviceCenterData = this.sb303Records.map(record => ({
          ...record.serviceCenterData,
          reportingStartDate: record.reportingStartDate,
          reportingEndDate: record.reportingEndDate
        }));

        this.clientData = this.sb303Records.map(record => ({
          ...record.clientData,
          reportingStartDate: record.reportingStartDate,
          reportingEndDate: record.reportingEndDate
        }));
        this.updateTables();
      }
    });
  }

  updateTables(): void {
    this.clientDataSource.data = this.clientData;
    this.clientDataSource.paginator = this.clientDataPaginator;
    this.clientDataSource.sort = this.clientDataSort;
    this.clientDataSource.sortingDataAccessor = this.customClientDataSortAccessor;

    this.serviceCenterDataSource.data = this.serviceCenterData;
    this.serviceCenterDataSource.paginator = this.serviceCenterDataPaginator;
    this.serviceCenterDataSource.sort = this.serviceCenterDataSort;
    this.serviceCenterDataSource.sortingDataAccessor = this.customServiceCenterDataSortAccessor;
  }

  closeReportingPeriod(): void {
    this.sharedService.openConfirm("Are you sure you want to close the reporting period? This will close the reporting period for all users and they will not be able to submit any more data. This action CANNOT be undone.");
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.sb303Service.closeReportingPeriod().subscribe({
            next: response => {
              if (response)
                this.toastr.success('Reporting period closed successfully');

              if (!response)
                this.toastr.error('Reporting period could not be closed');
            },
            error: e => console.log('error', e)
          });
        }
      }
    )
  }

  createReportingPeriod(): void {
    const dialogRef = this.dialog.open(DialogCreateNewReportComponent, {
      role: 'dialog',
      ariaLabel: "Create New Report",
      minWidth: !this.sharedService.mobile ? '800px' : '400px',
      maxWidth: !this.sharedService.mobile ? '1000px' : '800px',
      maxHeight: !this.sharedService.mobile ? '1000px' : '800px',
      panelClass: this.sharedService.darkMode ? "theme-dark" : "",
    });
  }

  printCompliancePDF(): void {
    const dialogRef = this.dialog.open(DialogPrintCompliancePDFComponent, {
      role: 'dialog',
      ariaLabel: "Print Compliance PDF",
      minWidth: !this.sharedService.mobile ? '800px' : '400px',
      maxWidth: !this.sharedService.mobile ? '1000px' : '800px',
      maxHeight: !this.sharedService.mobile ? '1000px' : '800px',
      panelClass: this.sharedService.darkMode ? "theme-dark" : "",
    });
  }

  //Client Data
  filterClientDataTable(event: Event) {
    let value = (event.target as HTMLInputElement).value;
    this.clientDataSource.filterPredicate = (data: any, filter: string) => {
      const isOpen = data.open ? 'open' : 'closed';
      const isSubmitted = data.submitted ? 'submitted' : 'not submitted';

      return isOpen.indexOf(filter) !== -1 ||
        isSubmitted.indexOf(filter) !== -1 ||
        JSON.stringify(data).toLowerCase().indexOf(filter) !== -1;
    };

    this.clientDataSource.filter = value;
  }

  customClientDataSortAccessor = (item: any, property: string) => {
    const direction = this.clientDataSort.direction;

    if (property === 'submittedDate') {
      const dateValue = item.submittedDate ? new Date(item.submittedDate) : null;
      if (dateValue === null) {
        return direction === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
      }
      return dateValue.getTime();
    }
    if (property === 'reportingStartDate') {
      const dateValue = item.reportingStartDate ? new Date(item.reportingStartDate) : null;
      if (dateValue === null) {
        return direction === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
      }
      return dateValue.getTime();
    }
    if (property === 'reportingEndDate') {
      const dateValue = item.reportingEndDate ? new Date(item.reportingEndDate) : null;
      if (dateValue === null) {
        return direction === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
      }
      return dateValue.getTime();
    }
    return item[property];
  };

  viewClientData(clientData: IClientData): void {
    const startDate = new Date(clientData.reportingStartDate);
    const cutoffDate = new Date(2026, 3, 1);
    this.showNewFields = startDate >= cutoffDate;

    const dialogRef = this.dialog.open(DialogViewClientDataComponent, {
      data: { showNewFields: this.showNewFields, id: clientData.id },
      role: 'dialog',
      ariaLabel: "Client Data",
      minWidth: !this.sharedService.mobile ? '800px' : '400px',
      maxWidth: !this.sharedService.mobile ? '1000px' : '800px',
      maxHeight: !this.sharedService.mobile ? '1000px' : '800px',
      panelClass: this.sharedService.darkMode ? "theme-dark" : "",
    });
  }

  openClientData(id: number): void {
    this.sharedService.openConfirm("Do you want to open the client data for editing?");
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.sb303Service.openClientData(id).subscribe({
            next: response => {
              if (response) {
                this.toastr.success('Client data opened successfully');
                this.clientData.find(data => data.id === id).open = true;
                this.clientData.find(data => data.id === id).submitted = false;
                this.updateTables();
              }
              if (!response)
                this.toastr.error('Client data could not be opened');
            },
            error: e => console.log('error', e)
          });
        }
      }
    )
  }

  closeClientData(id: number): void {
    this.sharedService.openConfirm("Do you want to close the client data for editing?");
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.sb303Service.closeClientData(id).subscribe({
            next: response => {
              if (response) {
                this.toastr.success('Client data closed successfully');
                this.clientData.find(data => data.id === id).open = false;
                this.updateTables();
              }
              if (!response)
                this.toastr.error('Client data could not be closed');
            },
            error: e => console.log('error', e)
          });
        }
      }
    )
  }

  openClientDataChangeLog(id: number, licenseId: string, licenseName: string): void {
    this.dialog.open(ClientDataAuditLogDialogComponent, {
      data: { entityId: id.toString(), licenseId: licenseId, licenseName: licenseName },
      minWidth: !this.sharedService.mobile ? '700px' : '300px',
      maxWidth: !this.sharedService.mobile ? '800px' : '300px',
      maxHeight: !this.sharedService.mobile ? '1000px' : '500px',
      panelClass: this.sharedService.darkMode ? "theme-dark" : ""
    });
  }

  openNotes(id: number, entityType: string): void {
    this.dialog.open(DialogSB303NotesComponent, {
      data: { entityId: id.toString(), entityType: entityType, editingNotes: this.authService.isAdmin || this.authService.isAnalyst },
      minWidth: !this.sharedService.mobile ? '700px' : '300px',
      maxWidth: !this.sharedService.mobile ? '800px' : '300px',
      maxHeight: !this.sharedService.mobile ? '1000px' : '500px',
      panelClass: this.sharedService.darkMode ? "theme-dark" : ""
    });
  }

  //Service Center Data
  filterServiceCenterDataTable(event: Event) {
    let value = (event.target as HTMLInputElement).value;
    this.serviceCenterDataSource.filterPredicate = (data: any, filter: string) => {
      const isOpen = data.open ? 'open' : 'closed';
      const isSubmitted = data.submitted ? 'submitted' : 'not submitted';

      return isOpen.indexOf(filter) !== -1 ||
        isSubmitted.indexOf(filter) !== -1 ||
        JSON.stringify(data).toLowerCase().indexOf(filter) !== -1;
    };

    this.serviceCenterDataSource.filter = value;
  }

  customServiceCenterDataSortAccessor = (item: any, property: string) => {
    const direction = this.serviceCenterDataSort.direction;

    if (property === 'submittedDate') {
      const dateValue = item.submittedDate ? new Date(item.submittedDate) : null;
      if (dateValue === null) {
        return direction === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
      }
      return dateValue.getTime();
    }
    if (property === 'reportingStartDate') {
      const dateValue = item.reportingStartDate ? new Date(item.reportingStartDate) : null;
      if (dateValue === null) {
        return direction === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
      }
      return dateValue.getTime();
    }
    if (property === 'reportingEndDate') {
      const dateValue = item.reportingEndDate ? new Date(item.reportingEndDate) : null;
      if (dateValue === null) {
        return direction === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
      }
      return dateValue.getTime();
    }
    return item[property];
  };

  viewServiceCenterData(serviceCenterData: IServiceCenterData): void {
    const startDate = new Date(serviceCenterData.reportingStartDate);
    const cutoffDate = new Date(2026, 3, 1);
    const averagesDate = new Date(2027, 3, 1);
    this.showNewFields = startDate >= cutoffDate;
    this.showAveragesFields = startDate >= averagesDate;

    const dialogRef = this.dialog.open(DialogViewServiceCenterDataComponent, {
      data: { showNewFields: this.showNewFields, showAveragesFields: this.showAveragesFields, id: serviceCenterData.id },
      role: 'dialog',
      ariaLabel: "Service Center Data",
      minWidth: !this.sharedService.mobile ? '1000px' : '600px',
      maxWidth: !this.sharedService.mobile ? '1200px' : '1000px',
      maxHeight: !this.sharedService.mobile ? '1200px' : '1000px',
      panelClass: this.sharedService.darkMode ? "theme-dark" : "",
    });
  }

  openServiceCenterData(id: number): void {
    this.sharedService.openConfirm("Do you want to open the service center data for editing?");
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.sb303Service.openServiceCenterData(id).subscribe({
            next: response => {
              if (response) {
                this.toastr.success('Service Center data opened successfully');
                this.serviceCenterData.find(data => data.id === id).open = true;
                this.serviceCenterData.find(data => data.id === id).submitted = false;
                this.updateTables();
              }
              if (!response)
                this.toastr.error('Service Center data could not be opened');
            },
            error: e => console.log('error', e)
          });
        }
      }
    )
  }

  closeServiceCenterData(id: number): void {
    this.sharedService.openConfirm("Do you want to close the service center data for editing?");
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.sb303Service.closeServiceCenterData(id).subscribe({
            next: response => {
              if (response) {
                this.toastr.success('Service Center data closed successfully');
                this.serviceCenterData.find(data => data.id === id).open = false;
                this.updateTables();
              }
              if (!response)
                this.toastr.error('Service Center data could not be closed');
            },
            error: e => console.log('error', e)
          });
        }
      }
    )
  }

  openServiceCenterDataChangeLog(id: number, licenseId: string, licenseName: string): void {
    this.dialog.open(ServiceCenterDataAuditLogDialogComponent, {
      data: { entityId: id.toString(), licenseId: licenseId, licenseName: licenseName },
      minWidth: !this.sharedService.mobile ? '700px' : '300px',
      maxWidth: !this.sharedService.mobile ? '800px' : '300px',
      maxHeight: !this.sharedService.mobile ? '1000px' : '500px',
      panelClass: this.sharedService.darkMode ? "theme-dark" : ""
    });
  }

  //Applicant Data
  loadApplicantData(page: number = 0): void {
    let form = this.applicantDataSearchForm.value;
    let search: IApplicantDataSearch = {
      page: page,
      name: form.name,
      licenseId: form.licenseId,
      submittedDate: form.submittedDate,
      sortBy: this.currentSort.active,
      sortDirection: this.currentSort.direction
    };

    this.applicantDataService.getApplicantData(search)
      .subscribe({
        next: response => {
          this.applicantDataSource.data = response.data;
          this.applicantDataSource.sort = this.applicantDataSort;
          this.applicantDataSource.sortingDataAccessor = this.customApplicantDataSortAccessor;
          this.totalApplicantRecords = response.totalRecords;
          this.applicantDataPaginator.pageIndex = page;
        },
        error: e => console.log('error', e)
      });
  }

  onPageChange(event: PageEvent): void {
    this.loadApplicantData(event.pageIndex);
  }

  onSortChange(sort: Sort): void {
    this.currentSort = sort;
    this.loadApplicantData(0);
  }

  customApplicantDataSortAccessor = (item: any, property: string) => {
    const direction = this.applicantDataSort.direction;
    if (property == 'name')
      return item.legalFirstName.toLowerCase();

    if (property === 'submittedDate') {
      const dateValue = item.submittedDate ? new Date(item.submittedDate) : null;
      if (dateValue === null) {
        return direction === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
      }
      return dateValue.getTime();
    }

    if (property == 'licenseId')
      return item.licenseId.toLowerCase();

    if (property == 'applicantStatus')
      return item.applicantStatus.toLowerCase();

    return item[property];
  };

  downloadAllApplicantData(): void {
    let fileName = `Required Applicant Data.csv`;

    this.applicantDataService.downloadAllApplicantData().subscribe(blob => {
      const url = window.URL.createObjectURL(blob);
      const download = document.createElement('a');
      download.href = url;
      download.download = fileName;
      download.click();
      window.URL.revokeObjectURL(url);
    });
  }

  //De-Identified Data
  filterDeIdentifiedTable(event: Event) {
    let value = (event.target as HTMLInputElement).value;
    this.deIdentifiedDataSource.filter = value.trim().toLowerCase();
  }

  updateDeIdentifiedTable(): void {
    this.deIdentifiedDataSource.data = this.sb303DeIdentifiedData;
    this.deIdentifiedDataSource.paginator = this.deIdentifiedDataPaginator;
    this.deIdentifiedDataSource.sort = this.deIdentifiedDataSort;
    this.deIdentifiedDataSource.sortingDataAccessor = this.customDeIdentifiedDataSortAccessor;
  }

  deleteDeIdentifiedData(id: number): void {
    this.sharedService.openConfirm("Are you sure you want to delete this De-Identified data?");
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.sb303Service.deleteDeIdentifiedData(id).subscribe({
            next: response => {
              if (response)
                this.toastr.success('De-Identified data deleted successfully');
              this.sb303DeIdentifiedData = this.sb303DeIdentifiedData.filter(data => data.id !== id);
              this.updateDeIdentifiedTable();
              if (!response)
                this.toastr.error('This De-Identified data could not be deleted');
            },
            error: e => console.log('error', e)
          });
        }
      });
  }

  downloadDeIdentifiedData(id: number): void {
    let deIdentifiedData = this.sb303DeIdentifiedData.find(data => data.id === id);
    let fileName = `${deIdentifiedData.startDate}-${deIdentifiedData.endDate} De-Identified Data.csv`

    this.sb303Service.downloadDeIdentifiedData(id).subscribe(blob => {
      const url = window.URL.createObjectURL(blob);
      const download = document.createElement('a');
      download.href = url;
      download.download = fileName;
      download.click();
      window.URL.revokeObjectURL(url);
    });
  }

  addDeIdentifiedData(data: ISB303DeIdentifiedData): void {
    this.sb303DeIdentifiedData.push(data);
    this.updateDeIdentifiedTable();
  }

  uploadDeIdentifiedData(): void {
    const dialogRef = this.dialog.open(DialogUploadDeIdentifiedDataComponent, {
      role: 'dialog',
      ariaLabel: "Upload De-Identified Data",
      minWidth: !this.sharedService.mobile ? '800px' : '400px',
      maxWidth: !this.sharedService.mobile ? '1000px' : '800px',
      maxHeight: !this.sharedService.mobile ? '1000px' : '800px',
      panelClass: this.sharedService.darkMode ? "theme-dark" : "",
    });

    dialogRef.afterClosed().subscribe((response) => {
      if (!this.sharedService.isCancelled(response)) {
        this.toastr.success('De-Identified data uploaded successfully');
        this.addDeIdentifiedData(response);
      }
    });
  }

  customDeIdentifiedDataSortAccessor = (item: any, property: string) => {
    const direction = this.deIdentifiedDataSort.direction;

    if (property === 'startDate') {
      const dateValue = item.startDate ? new Date(item.startDate) : null;
      if (dateValue === null) {
        return direction === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
      }
      return dateValue.getTime();
    }
    if (property === 'endDate') {
      const dateValue = item.endDate ? new Date(item.endDate) : null;
      if (dateValue === null) {
        return direction === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
      }
      return dateValue.getTime();
    }
    return item[property];
  };

  //Non-Reporting Licenses
  filterLicensesTable(event: Event) {
    let value = (event.target as HTMLInputElement).value;
    this.licenseSource.filter = value.trim().toLowerCase();
  }

  updateNonReportingLicensesTable(): void {
    this.licenseSource.data = this.nonReportingLicenses;
    this.licenseSource.paginator = this.licensePaginator;
    this.licenseSource.sort = this.licenseSort;
    this.licenseSource.sortingDataAccessor = this.customNonReportingLicenseSortAccessor;
  }

  customNonReportingLicenseSortAccessor = (item: any, property: string) => {
    const direction = this.licenseSort.direction;

    if (property === 'reportingStartDate') {
      const dateValue = item.reportingStartDate ? new Date(item.reportingStartDate) : null;
      if (dateValue === null) {
        return direction === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
      }
      return dateValue.getTime();
    }
    if (property === 'reportingEndDate') {
      const dateValue = item.reportingEndDate ? new Date(item.reportingEndDate) : null;
      if (dateValue === null) {
        return direction === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
      }
      return dateValue.getTime();
    }
    return item[property];
  };

  downloadAllData(): void {
    const dialogRef = this.dialog.open(DialogDownloadReportingWindowDataComponent, {
      role: 'dialog',
      ariaLabel: "Download Reporting Window Data",
      minWidth: !this.sharedService.mobile ? '800px' : '400px',
      maxWidth: !this.sharedService.mobile ? '1000px' : '800px',
      maxHeight: !this.sharedService.mobile ? '1000px' : '800px',
      panelClass: this.sharedService.darkMode ? "theme-dark" : "",
    });
  }

  downloadIndividualServiceCenterData(): void {
    const dialogRef = this.dialog.open(DialogDownloadIndividualServiceCenterDataComponent, {
      role: 'dialog',
      ariaLabel: "Download Individual Service Center Data",
      minWidth: !this.sharedService.mobile ? '800px' : '400px',
      maxWidth: !this.sharedService.mobile ? '1000px' : '800px',
      maxHeight: !this.sharedService.mobile ? '1000px' : '800px',
      panelClass: this.sharedService.darkMode ? "theme-dark" : "",
    });
  }

  changePartialQuarter(id: number, entityType: string, partialQuarter: boolean): void {
    if(!(this.authService.isAdmin || this.authService.isAnalyst))
      return;

    if(partialQuarter) {
      this.sharedService.openConfirm("Are you sure you want to set this reporting window as a full quarter?");
    }

    if(!partialQuarter) {
      this.sharedService.openConfirm("Are you sure you want to set this reporting window as a partial quarter?");
    }

    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.sb303Service.changePartialQuarter(id, entityType).subscribe({
            next: response => {
              if (response) {
                if(entityType === 'clientData') {
                  this.clientData.find(data => data.id === id).partialQuarter = !partialQuarter;
                  this.serviceCenterData.find(data => data.id === this.sb303Records.find(record => record.clientData.id === id).serviceCenterData.id).partialQuarter = !partialQuarter;
                }
                if(entityType === 'serviceCenterData') {
                  this.serviceCenterData.find(data => data.id === id).partialQuarter = !partialQuarter;
                  this.clientData.find(data => data.id === this.sb303Records.find(record => record.serviceCenterData.id === id).clientData.id).partialQuarter = !partialQuarter;
                }
                this.updateTables();
                this.toastr.success('Partial quarter status updated successfully');
              }

              if (!response)
                this.toastr.error('Could not update the partial quarter status');
            },
            error: e => console.log('error', e)
          });
        }
      });
  }
}
